import React, { useMemo } from 'react';
import Table from '../../../Components/Tables/Table/Table';
import TableWrapper from '../../../Components/Tables/TableWrapper/TableWrapper';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from '../MarketIntelligenceReports.module.scss';
import { MarketPriceSymbolContractCompositeModel } from '../MarketIntelligenceReportsDefinitions';
import { spreadTableHeaders, SpreadTableRow } from './MarketIntelligenceTableDefinitions';

const defaultMinimumTableWidth = 700;

interface IMarketIntelligenceSpreadTableProps {
    symbolContracts: MarketPriceSymbolContractCompositeModel[];
    loading: boolean;
    testId?: string;
}

const MarketIntelligenceSpreadTable: React.FC<IMarketIntelligenceSpreadTableProps> = (props: IMarketIntelligenceSpreadTableProps) => {
    // Application hooks.
    const [translations] = useLanguage();

    // Text hook.
    const titleString = useMemo(() => `${props.symbolContracts[0].commodityDisplayName} Spread`, [props.symbolContracts]);

    const getSpreadOrEmpty = (value1?: number | null, value2?: number | null) => {
        if (value1 && value2 && value1 !== null && value2 !== null) {
            return value1 - value2;
        }

        return 0;
    };

    const spreadTableRows = useMemo(() => {
        if (!props.symbolContracts) {
            return [];
        }

        return props.symbolContracts
            .map((x, index) => {
                if (index > props.symbolContracts.length - 2) {
                    return null;
                }

                const comparableContract = props.symbolContracts[index + 1];

                return {
                    reutersInstrumentCode: x.reutersInstrumentCode,
                    spreadName: `${x.monthYearName} / ${comparableContract.monthYearName}`,
                    previousClose: getSpreadOrEmpty(x.settlementPrice, comparableContract.settlementPrice),
                    open: getSpreadOrEmpty(x.openInterest, comparableContract.openInterest),
                    highPrice: getSpreadOrEmpty(x.highPrice, comparableContract.highPrice),
                    lowPrice: getSpreadOrEmpty(x.lowPrice, comparableContract.lowPrice),
                    lastPrice: getSpreadOrEmpty(x.latestPrice, comparableContract.latestPrice),
                    openInterestNetChange: getSpreadOrEmpty(x.openInterestNetChange, comparableContract.openInterestNetChange),
                    volume: getSpreadOrEmpty(x.volume, comparableContract.volume),
                };
            })
            .filter((x) => x !== null);
    }, [props.symbolContracts]) as SpreadTableRow[];

    return (
        <div data-testid={props.testId} className={styles.market_intelligence_reports_table}>
            <TableWrapper title={titleString} isLoading={props.loading} track={{ eventCategory: 'MarketIntelligenceReports', eventAction: 'ListSpreads' }}>
                <Table
                    tableHeaders={spreadTableHeaders}
                    tableData={spreadTableRows!}
                    tableStyle="alternatingRows"
                    minimumTableWidth={defaultMinimumTableWidth}
                />
            </TableWrapper>
        </div>
    );
};

export default MarketIntelligenceSpreadTable;
